import { computed, ref, useStore } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { useI18n } from "@/composables";
import { getLtksStore } from "@/store/ltks";
import mapData from "@/utils/transform/map-data";

import useConsumerSearchApi from "./-api";
import { getProfilesStore } from "../profiles";

interface RetailerProductLinksStoreContext {
  $api: ReturnType<typeof useConsumerSearchApi>;
}

export const useConsumerSearchStore = defineStore(
  "consumer-search",
  () => {
    const store = useStore();
    const ltksStore = getLtksStore(store);
    const profilesStore = getProfilesStore(store);
    const i18n = useI18n();

    // State
    const ltksMap = ref<ConsumerSearchPostIDResultMap>({});
    const productsMap = ref<ConsumerSearchProductIDResultMap>({});
    const profilesMap = ref<ConsumerSearchProfileIDResultMap>({});
    const ltks = ref<ConsumerSearchResponsePostID[]>([]);
    const ltksTotal = ref<number>(0);
    const ltksComplete = ref<boolean>(false);
    const profiles = ref<ConsumerSearchResponseProfileID[]>([]);
    const profilesTotal = ref<number>(0);
    const profilesComplete = ref<boolean>(false);
    const queryType = ref<SearchEventQueryType>();

    const locale = computed(() => i18n.localeProperties.iso);
    const countryCode = computed(() => {
      const localeValue = locale.value;
      if (!localeValue) {
        return;
      }
      return new Intl.Locale(localeValue).region;
    });

    // Actions
    async function fetchLtkSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      resetLtks();
      const response = await this.$api.fetchLtkIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newLtks = response.data.items;
      ltks.value = newLtks;
      ltksTotal.value = response.data.total;
      ltksComplete.value = response.data.complete;
      ltksMap.value = Object.assign({}, ltksMap.value, mapData("id", newLtks));
      productsMap.value = Object.assign(
        {},
        productsMap.value,
        mapData(
          "id",
          newLtks.flatMap((item) => item.children || []),
        ),
      );
      if (process.server) {
        await ltksStore.fetchLtksByIds(newLtks.map((item) => item.id));
      }
    }

    async function fetchMoreLtkSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchLtkIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newLtks = response.data.items;
      await ltksStore.fetchLtksByIds(newLtks.map((item) => item.id));
      ltks.value = [...ltks.value, ...newLtks];
      ltksTotal.value = response.data.total;
      ltksComplete.value = response.data.complete;
      ltksMap.value = Object.assign({}, ltksMap.value, mapData("id", newLtks));
      productsMap.value = Object.assign(
        {},
        productsMap.value,
        mapData(
          "id",
          newLtks.flatMap((item) => item.children || []),
        ),
      );
    }

    // Actions
    async function fetchProfileSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      resetProfiles();
      const response = await this.$api.fetchProfileIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newProfiles = response.data.items;
      profiles.value = newProfiles;
      await profilesStore.fetchProfilesByIds({ ids: newProfiles.map((item) => item.id) });
      profilesTotal.value = response.data.total;
      profilesComplete.value = response.data.complete;
      profilesMap.value = Object.assign({}, profilesMap.value, mapData("id", newProfiles));
    }

    async function fetchMoreProfileSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchProfileIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newProfiles = response.data.items;
      await profilesStore.fetchProfilesByIds({ ids: newProfiles.map((item) => item.id) });
      profiles.value = [...profiles.value, ...newProfiles];
      profilesTotal.value = response.data.total;
      profilesComplete.value = response.data.complete;
      profilesMap.value = Object.assign({}, profilesMap.value, mapData("id", newProfiles));
    }

    function resetLtks() {
      ltks.value = [];
      ltksTotal.value = 0;
      ltksComplete.value = false;
    }

    function resetProfiles() {
      profiles.value = [];
      profilesTotal.value = 0;
      profilesComplete.value = false;
    }

    function resetStore() {
      ltks.value = [];
      ltksTotal.value = 0;
      ltksComplete.value = false;
      profiles.value = [];
      profilesTotal.value = 0;
      profilesComplete.value = false;
    }

    return {
      ltksMap,
      productsMap,
      profilesMap,
      ltks,
      ltksTotal,
      ltksComplete,
      profiles,
      profilesTotal,
      profilesComplete,
      queryType,
      fetchLtkSearchResults,
      fetchMoreLtkSearchResults,
      fetchProfileSearchResults,
      fetchMoreProfileSearchResults,
      resetLtks,
      resetProfiles,
      resetStore,
    };
  },
  {
    api: useConsumerSearchApi,
  },
);
